import { Box, Button, Grid, Typography } from "@mui/material";
import React, { FC, useEffect, useState } from "react";
import { CalculatorTextFieldItem } from "../components/CalculatorTextFieldItem";

const Fibrosis4IndexComponent: FC = () => {
  const initialState = {
    age: "",
    ast: "",
    alt: "",
    plateletCount: "",
  };

  const [values, setValues] = useState(initialState);
  const [fibrosis4Index, setFibrosis4Index] = useState<number | null>(null);
  const [riskLevel, setRiskLevel] = useState<string | null>(null);
  const [riskColor, setRiskColor] = useState<
    "success" | "warning" | "error" | undefined
  >();
  const [ageError, setAgeError] = useState<string | null>(null);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    // Allow empty string or valid numbers
    if (value === "" || !isNaN(Number(value))) {
      const parsed = value === "" ? 0 : parseFloat(value);

      if (name === "age") {
        if (parsed !== 0 && (parsed < 1 || parsed > 120)) {
          setAgeError("Age must be between 1 and 120");
        } else {
          setAgeError(null);
        }
      }

      setValues((prev) => ({ ...prev, [name]: value }));
    }
  };

  useEffect(() => {
    const { age, ast, alt, plateletCount } = values;

    // Convert string values to numbers for calculation
    const ageNum = parseFloat(age) || 0;
    const astNum = parseFloat(ast) || 0;
    const altNum = parseFloat(alt) || 0;
    const plateletCountNum = parseFloat(plateletCount) || 0;

    // Guard against invalid or missing inputs
    if (
      altNum <= 0 ||
      plateletCountNum <= 0 ||
      ageNum <= 0 ||
      astNum <= 0 ||
      ageError
    ) {
      setFibrosis4Index(null);
      setRiskLevel(null);
      setRiskColor(undefined);
      return;
    }

    const index = (ageNum * astNum) / (plateletCountNum * Math.sqrt(altNum));
    setFibrosis4Index(index);

    if (index < 1.45) {
      setRiskLevel("Low risk of fibrosis");
      setRiskColor("success");
    } else if (index <= 3.25) {
      setRiskLevel("Intermediate risk of fibrosis");
      setRiskColor("warning");
    } else {
      setRiskLevel("High risk of fibrosis");
      setRiskColor("error");
    }
  }, [values, ageError]);

  // const resetForm = () => {
  //   setValues(initialState);
  //   setFibrosis4Index(null);
  //   setRiskLevel(null);
  //   setRiskColor(undefined);
  //   setAgeError(null);
  // };

  const fieldConfig = {
    age: { label: "Age", unit: "years", placeholder: "Norm: 18-100" },
    ast: { label: "AST", unit: "U/L", placeholder: "Norm: 15-41" },
    alt: { label: "ALT", unit: "U/L", placeholder: "Norm: 5-35" },
    plateletCount: {
      label: "Platelet Count",
      unit: "10⁹/L",
      placeholder: "Norm: 150-350",
    },
  };

  const incomplete = Object.values(values).some((value) => value === "");

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <Box flex={1} overflow="auto" p={2}>
        <Grid container spacing={2} direction="column">
          {Object.entries(fieldConfig).map(
            ([key, { label, unit, placeholder }]) => (
              <Grid item xs={12} key={key}>
                <CalculatorTextFieldItem
                  name={key}
                  text={label}
                  value={values[key as keyof typeof values]}
                  placeholder={placeholder}
                  endAdornment={<Typography>{unit}</Typography>}
                  onChange={handleChange}
                  error={key === "age" && !!ageError}
                  helperText={key === "age" ? ageError : ""}
                />
              </Grid>
            ),
          )}
        </Grid>
      </Box>

      <Box
        p={2}
        sx={{
          borderTop: 1,
          borderColor: "#D3D3D3",
        }}
      >
        <Typography variant="body2" sx={{ mt: 2 }}>
          <strong>Calculation:</strong> ({parseFloat(values.age) || 0} ×{" "}
          {parseFloat(values.ast) || 0}) / (
          {parseFloat(values.plateletCount) || 0} × √
          {parseFloat(values.alt) || 0})
        </Typography>

        <Typography variant="h5">Result</Typography>
        <Typography>
          {incomplete ? (
            <i>Please fill out the required fields</i>
          ) : (
            <>
              {fibrosis4Index !== null && (
                <Typography color={riskColor}>
                  <b>Score: {fibrosis4Index.toFixed(2)}</b> ({riskLevel})
                </Typography>
              )}
            </>
          )}
        </Typography>
      </Box>
    </Box>
  );
};

export default Fibrosis4IndexComponent;
